import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Grid, Radio, RadioGroup, FormControlLabel, Button, Box, CircularProgress, Stack, TextField, Collapse, Typography } from '@mui/material';
import AlertDialog from '../../AlertDialog/AlertDialog';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { getMemberBasicInfo } from '../../../store/memberDetail.slice';
import { RootState } from '../../../reducers';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import { copyObject, preventTextInput } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { fetchSexualHealthById, saveSexualHealth } from '../../../store/hra.slice';
import { SexualOrientations, YesNoAnswers } from '../../../utils/assessments';
import { GenderEnum } from '../../../Enum/GenderEnum';

const SexualHealthAssessment: React.FC<{ memberId: string }> = ({ memberId }) => {
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { sexualHealth, isFetching, isSaving: isSavingHra } = useSelector((state: RootState) => state.hra);
    const { engagementDraft, memberAssessmentHistory, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const { memberBasicInfo } = useSelector((state: RootState) => state.memberDetail);
    const isSaving = isSavingHra || isSavingEngagementDraft;

    useEffect(() => {
        if (Boolean(engagementDraft.SexualHealthId)) {
            dispatch(fetchSexualHealthById(engagementDraft.SexualHealthId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, memberId]);

    useEffect(() => {
        dispatch(getMemberBasicInfo(memberId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, memberId]);

    const handleOnClickSave = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (isWithoutComplete = false) => {
        if (isWithoutComplete) {
            dispatch(saveSexualHealth({ ...sexualHealth, Status: StatusEnum.Completed }, true));
        } else {
            dispatch(gotoNextMenu());
        }
    };

    const handleOnChangeAnswer = (answer: any, propName: string) => {
        let data = copyObject(sexualHealth);
        data[propName] = answer;
        data.MemberId = memberId;

        dispatch(saveSexualHealth(data));
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel" sx={{ pt: 4 }}>
            {!Boolean(memberBasicInfo.Gender) ||
            (memberBasicInfo.Gender !== GenderEnum.MaleDescription && memberBasicInfo.Gender !== GenderEnum.FemaleDescription) ? (
                <Stack alignItems="center" sx={{ pb: 4 }}>
                    <Typography variant="body1" component="label" color="red">
                        Please set Gender for this member before taking this assessment {memberBasicInfo.Gender}
                    </Typography>
                </Stack>
            ) : isFetching ? (
                <Box display="flex" justifyContent="center" className="loader-container">
                    <CircularProgress />
                </Box>
            ) : (
                <React.Fragment>
                    <Grid className="content-form">
                        <div>
                            <Typography variant="h6" component="label">
                                {memberBasicInfo.Gender} Health
                            </Typography>
                            <hr />
                        </div>
                        <Grid container spacing={2}>
                            <Grid size={{ sm: 6, md: 6, lg: 6 }} display="flex" alignItems="center">
                                <Typography variant="subtitle1" component="label">
                                    Have you been sexually active in the past year?
                                </Typography>
                            </Grid>
                            <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                <RadioGroup
                                    row
                                    name="IsActiveInPastYear"
                                    value={String(sexualHealth.IsActiveInPastYear)}
                                    onChange={(e) => handleOnChangeAnswer(e.target.value === 'true', e.target.name)}
                                >
                                    {YesNoAnswers.map((type, index) => {
                                        return <FormControlLabel key={`is-active-${index}`} value={type.Id} control={<Radio />} label={type.Name} />;
                                    })}
                                </RadioGroup>
                            </Grid>
                        </Grid>
                        <Collapse in={sexualHealth.IsActiveInPastYear} timeout="auto" unmountOnExit>
                            <React.Fragment>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid size={0.5} />
                                    <Grid size={{ sm: 5.5 }} display="flex" alignItems="center">
                                        <Typography variant="subtitle1" component="label">
                                            Do you have sex with men, women, or both?
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                        <RadioGroup
                                            row
                                            name="SexualOrientation"
                                            value={sexualHealth.SexualOrientation}
                                            onChange={(e) => handleOnChangeAnswer(e.target.value, e.target.name)}
                                        >
                                            {SexualOrientations.map((type, index) => {
                                                return (
                                                    <FormControlLabel
                                                        key={`sexual-orientation-${index}-${index}`}
                                                        value={type.Id}
                                                        control={<Radio />}
                                                        label={type.Name}
                                                    />
                                                );
                                            })}
                                        </RadioGroup>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid size={0.5} />
                                    <Grid size={{ sm: 5.5 }} display="flex" alignItems="center">
                                        <Typography variant="subtitle1" component="label">
                                            In the past 12 months, how many sexual partners have you had?**
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                        <TextField
                                            variant="outlined"
                                            name="PartnersInPastTwelveMonths"
                                            className="hide-textinput-label"
                                            placeholder="Enter number here"
                                            type="number"
                                            onKeyPress={preventTextInput}
                                            inputProps={{ min: 0 }}
                                            defaultValue={sexualHealth.PartnersInPastTwelveMonths}
                                            onBlur={(e) => handleOnChangeAnswer(e.target.value, e.target.name)}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid size={0.5} />
                                    <Grid size={{ sm: 5.5 }} display="flex" alignItems="center">
                                        <Typography variant="subtitle1" component="label">
                                            Refer to {`${memberBasicInfo.Gender === GenderEnum.MaleDescription ? 'PCP' : 'GYN'}`} for further Sexual Health
                                            analysis
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                        <RadioGroup
                                            row
                                            name="ReferToPcp"
                                            value={String(sexualHealth.ReferToPcp)}
                                            onChange={(e) => handleOnChangeAnswer(e.target.value === 'true', e.target.name)}
                                        >
                                            {YesNoAnswers.map((type, index) => {
                                                return <FormControlLabel key={`is-active-${index}`} value={type.Id} control={<Radio />} label={type.Name} />;
                                            })}
                                        </RadioGroup>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        </Collapse>
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end" className="content-footer">
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 3 }}
                            onClick={() => dispatch(gotoPreviousMenu())}
                            startIcon={<ArrowBackIosIcon />}
                        >
                            Previous
                        </Button>
                        <Button type="submit" variant="contained" onClick={handleOnClickSave} endIcon={<ArrowForwardIosIcon />}>
                            Next
                        </Button>
                    </Stack>
                </React.Fragment>
            )}
            {Boolean(engagementDraft.SexualHealthId) ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Sexual Health"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    onSkip={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    message="I confirm and acknowledge that Sexual health is completed."
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="Sexual Health"
                    cancelText="No"
                    okText="Yes, Proceed"
                    onConfirm={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    message="Are you sure to proceed without completing Sexual health?"
                />
            )}
        </Grid>
    );
};

export default SexualHealthAssessment;
